import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Settings, Eye, ShoppingCart } from 'lucide-react';
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import ImageUploader from './ImageUploader';
import SmartImage from '../SmartImage';

const ModificationsManagement = () => {
  const [modifications, setModifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMod, setEditingMod] = useState(null);
  const [modImage, setModImage] = useState('');

  useEffect(() => {
    loadModifications();
  }, []);

  const loadModifications = async () => {
    setLoading(true);
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTIONS.MODIFICATIONS));
      const mods = [];
      querySnapshot.forEach((doc) => {
        mods.push({ id: doc.id, ...doc.data() });
      });
      setModifications(mods);
    } catch (error) {
      console.error('Error loading modifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const modData = {
      name: formData.get('name'),
      price: formData.get('price'),
      description: formData.get('description'),
      icon: modImage || editingMod?.icon || '⚙️',
      features: formData.get('features')?.split(',').map(f => f.trim()) || [],
      benefits: formData.get('benefits')?.split(',').map(b => b.trim()) || [],
      duration: formData.get('duration') || '',
      warranty: formData.get('warranty') || '',
      type: formData.get('type') || 'performance',
      active: true,
      createdAt: Timestamp.now()
    };

    try {
      if (editingMod) {
        // التحقق من وجود التعديل قبل التحديث
        try {
          await updateDoc(doc(db, COLLECTIONS.MODIFICATIONS, editingMod.id), modData);
        } catch (updateError) {
          if (updateError.code === 'not-found') {
            alert('هذا التعديل غير موجود في قاعدة البيانات. سيتم إضافته كتعديل جديد.');
            await addDoc(collection(db, COLLECTIONS.MODIFICATIONS), modData);
          } else {
            throw updateError;
          }
        }
      } else {
        await addDoc(collection(db, COLLECTIONS.MODIFICATIONS), modData);
      }
      setShowModal(false);
      setEditingMod(null);
      setModImage('');
      loadModifications();
    } catch (error) {
      console.error('Error saving modification:', error);
      alert(`حدث خطأ أثناء حفظ التعديل: ${error.message}`);
    }
  };

  const handleDelete = async (modId) => {
    if (window.confirm('هل أنت متأكد من حذف هذا التعديل؟')) {
      try {
        await deleteDoc(doc(db, COLLECTIONS.MODIFICATIONS, modId));
        // حذف محلي لتحديث الواجهة فوراً
        setModifications(prev => prev.filter(m => m.id !== modId));
      } catch (error) {
        console.error('Error deleting modification:', error);
        if (error.code === 'not-found') {
          // إذا لم يكن التعديل موجوداً في Firebase، نزيله من الحالة المحلية فقط
          setModifications(prev => prev.filter(m => m.id !== modId));
          alert('تم حذف التعديل من الواجهة (لم يكن موجوداً في قاعدة البيانات).');
        } else {
          alert(`حدث خطأ أثناء حذف التعديل: ${error.message}`);
        }
      }
    }
  };

  const modTypes = [
    { value: 'performance', label: 'أداء وقوة', color: 'red' },
    { value: 'suspension', label: 'نظام التعليق', color: 'blue' },
    { value: 'exhaust', label: 'نظام العادم', color: 'orange' },
    { value: 'exterior', label: 'تعديلات خارجية', color: 'purple' },
    { value: 'interior', label: 'تعديلات داخلية', color: 'green' },
    { value: 'electronics', label: 'إلكترونيات', color: 'cyan' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة التعديلات</h1>
          <p className="text-gray-600 mt-2">إجمالي التعديلات: {modifications.length}</p>
        </div>
        <button
          onClick={() => {
            setEditingMod(null);
            setModImage('');
            setShowModal(true);
          }}
          className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 font-medium transition-colors"
        >
          <Plus className="w-5 h-5" />
          إضافة تعديل جديد
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {modTypes.map((type) => {
          const count = modifications.filter(m => m.type === type.value).length;
          return (
            <div key={type.value} className={`bg-${type.color}-50 rounded-xl shadow-md p-4 border border-${type.color}-200`}>
              <p className={`text-sm text-${type.color}-700`}>{type.label}</p>
              <p className={`text-2xl font-bold text-${type.color}-800`}>{count}</p>
            </div>
          );
        })}
      </div>

      {modifications.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modifications.map((mod) => {
            const modType = modTypes.find(t => t.value === mod.type) || modTypes[0];
            return (
              <div key={mod.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
                <div className={`bg-gradient-to-r from-${modType.color}-500 to-${modType.color}-600 p-6 text-white`}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-16 h-16 flex items-center justify-center">
                      <SmartImage
                        src={mod.icon}
                        alt={mod.name}
                        size="large"
                        fallback="⚙️"
                        className="w-full h-full object-cover rounded-lg"
                      />
                    </div>
                    <div>
                      <span className="text-xs opacity-75">{modType.label}</span>
                      <h3 className="text-xl font-bold">{String(mod.name || '')}</h3>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-2xl font-bold">{String(mod.price || '')}</p>
                    {mod.duration && <p className="text-sm opacity-90">{String(mod.duration)}</p>}
                  </div>
                </div>

                <div className="p-6">
                  {mod.description && (
                    <p className="text-gray-600 text-sm mb-4">{String(mod.description)}</p>
                  )}

                  {mod.features && mod.features.length > 0 && (
                    <div className="mb-4">
                      <p className="text-sm font-bold text-gray-700 mb-2">المميزات:</p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {mod.features.slice(0, 3).map((item, idx) => (
                          <li key={idx} className="flex items-center gap-2">
                            <span className="text-green-500">✓</span>
                            {String(item)}
                          </li>
                        ))}
                        {mod.features.length > 3 && (
                          <li className="text-xs text-gray-500">+{mod.features.length - 3} المزيد...</li>
                        )}
                      </ul>
                    </div>
                  )}

                  {mod.warranty && (
                    <div className="text-sm text-gray-600 mb-4">
                      <span className="font-bold">الضمان:</span> {String(mod.warranty)}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setEditingMod(mod);
                        setModImage(mod.icon || '');
                        setShowModal(true);
                      }}
                      className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      تعديل
                    </button>
                    <button
                      onClick={() => handleDelete(mod.id)}
                      className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      حذف
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <Settings className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا توجد تعديلات متاحة</p>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-3xl w-full p-6 my-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {editingMod ? 'تعديل التعديل' : 'إضافة تعديل جديد'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">اسم التعديل</label>
                  <input
                    type="text"
                    name="name"
                    defaultValue={editingMod?.name}
                    required
                    placeholder="مثال: تعديل المحرك الرياضي"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">السعر</label>
                  <input
                    type="text"
                    name="price"
                    defaultValue={editingMod?.price}
                    required
                    placeholder="مثال: من 1500$"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">النوع</label>
                  <select
                    name="type"
                    defaultValue={editingMod?.type || 'performance'}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  >
                    {modTypes.map((type) => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 text-right">المدة</label>
                  <input
                    type="text"
                    name="duration"
                    defaultValue={editingMod?.duration}
                    placeholder="مثال: 2-3 أيام"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                  />
                </div>
              </div>

              <ImageUploader
                currentImage={modImage || editingMod?.icon}
                onImageChange={setModImage}
                label="صورة أو رمز التعديل"
              />

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الوصف</label>
                <textarea
                  name="description"
                  defaultValue={editingMod?.description}
                  rows="3"
                  placeholder="وصف تفصيلي للتعديل..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">المميزات (افصل بفاصلة)</label>
                <input
                  type="text"
                  name="features"
                  defaultValue={editingMod?.features?.join(', ')}
                  placeholder="زيادة القوة, تحسين الأداء, صوت رياضي"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الفوائد (افصل بفاصلة)</label>
                <input
                  type="text"
                  name="benefits"
                  defaultValue={editingMod?.benefits?.join(', ')}
                  placeholder="أداء أفضل, استهلاك أقل, مظهر جذاب"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 text-right">الضمان</label>
                <input
                  type="text"
                  name="warranty"
                  defaultValue={editingMod?.warranty}
                  placeholder="مثال: سنة واحدة أو 20,000 كم"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-right"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="submit"
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-medium transition-colors"
                >
                  {editingMod ? 'حفظ التعديلات' : 'إضافة التعديل'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingMod(null);
                    setModImage('');
                  }}
                  className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-3 rounded-lg font-medium transition-colors"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModificationsManagement;
